<template>
    <div id="cancellation">
        <Teaser :background-url="teaserImageUrl" class="mb-3" width="1024" height="480">
            <DetailsTeaser />
        </Teaser>

        <Form :errors="errors" @submitValid="conditionalSubmit" ref="form">
            <LayoutWithAside reorder-mobile sticky-aside>
                <template v-slot:main>
                    <template v-if="loading">
                        <TopSection
                            v-if="initialState || nonCancelableState || success || unknownVoucherCode"
                            class="mb-3 single-section"
                        >
                            <template v-slot:header-wrapper>
                                <h1 class="section-header text-uppercase text-primary title">
                                    {{ $t('Cancellation.header') }}
                                </h1>
                            </template>
                        </TopSection>
                        <div class="pending-block">
                            <IconSvg filepath="spinner.svg" class="proceed-indicator" />
                            <p>{{ $t('Generic.pleaseWait') }}...</p>
                        </div>
                    </template>
                    <template v-else>
                        <TopSection
                            v-if="initialState || nonCancelableState || success || unknownVoucherCode"
                            class="mb-3 single-section"
                        >
                            <template v-slot:header-wrapper>
                                <h1 class="section-header text-uppercase text-primary title">
                                    {{ $t('Cancellation.header') }}
                                </h1>
                            </template>

                            <template>
                                <CancellationInitForm v-if="initialState" :code.sync="code" />

                                <UnknownVoucherCode
                                    v-else-if="unknownVoucherCode"
                                    :supportPhone="supportPhone"
                                    :supportEmail="supportEmail"
                                    @back="response = {}"
                                />

                                <NonCancelableText
                                    v-else-if="!!nonCancelableState"
                                    :state="nonCancelableState"
                                    :productData="response.productData"
                                    :valueVoucher="response.valueVoucher"
                                    :supportPhone="supportPhone"
                                    :supportEmail="supportEmail"
                                    @back="response = {}"
                                />

                                <SuccessText
                                    v-else-if="success"
                                    :supportPhone="supportPhone"
                                    :supportEmail="supportEmail"
                                />
                            </template>
                        </TopSection>

                        <template v-else>
                            <ValueVoucherCancellation
                                v-if="response.valueVoucher"
                                :cancellation-info="response"
                                :cancellation="cancellation"
                                @submitCancellation="submitCancellation"
                            >
                            </ValueVoucherCancellation>

                            <HotelVoucherCancellation
                                v-else
                                :cancellation-info="response"
                                :cancellation="cancellation"
                                @submitCancellation="submitCancellation"
                            >
                            </HotelVoucherCancellation>
                        </template>

                        <p v-if="initialState" class="small">
                            <translate path="Contact.gdpr">
                                <template v-slot:link>
                                    <nuxt-link
                                        :to="{ name: 'category', params: { category: 'datenschutz' } }"
                                        rel="nofollow"
                                        target="_blank"
                                    >
                                        {{ $t('DataProtection.link') }}
                                    </nuxt-link>
                                </template>
                            </translate>

                            <br />

                            <translate path="Cancellation.withdrawal">
                                <template v-slot:link>
                                    <nuxt-link
                                        :to="{ name: 'category', params: { category: 'widerrufsrecht' } }"
                                        rel="nofollow"
                                        target="_blank"
                                    >
                                        {{ $t('Cancellation.withdrawal.viewAgain') }}
                                    </nuxt-link>
                                </template>
                            </translate>
                        </p>

                        <div v-if="pending" class="pending-block">
                            <IconSvg filepath="spinner.svg" class="proceed-indicator" />
                            <p>{{ $t('Generic.pleaseWait') }}...</p>
                        </div>
                    </template>
                </template>

                <template v-if="!loading && !nonCancelableState && !unknownVoucherCode && response.productData" v-slot:aside>
                    <OrderAside>
                        <media-only desktop>
                            <Teaser :background-url="response.productData.imageUrl" class="aside-teaser">
                                <DetailsTeaser>
                                    <Classification :hotelClassification="response.productData.classification || {}" />
                                    <div class="hotel-name">{{ response.productData.hotel }}</div>
                                </DetailsTeaser>
                            </Teaser>
                        </media-only>

                        <DetailsSection>
                            <h1 class="heading">
                                {{ response.productData.title }}
                            </h1>
                        </DetailsSection>

                        <DetailsSection class="darker">
                            <template v-slot:header>
                                {{ $t('VoucherDetails.section.services') }}
                            </template>

                            <p class="inline-justified mb-1">
                                <span>{{ $t('Cancellation.code.placeholder') }}:</span>
                                <span
                                    ><strong>{{ response.code }}</strong></span
                                >
                            </p>

                            <VariantData v-if="!response.valueVoucher" :variantData="variantData" inline />
                        </DetailsSection>

                        <DetailsSection v-if="response.productData.arrivalDate">
                            <template v-slot:header>
                                {{ $t('Generic.travelPeriod') }}
                            </template>
                            <p class="inline-justified indented mb-1">
                                <span>{{ $t('Generic.checkIn') }}:</span>
                                <span>{{ response.productData.arrivalDate }}</span>
                            </p>
                            <p class="inline-justified indented">
                                <span>{{ $t('Generic.duration') }}:</span>
                                <span>{{ response.productData.duration }}</span>
                            </p>
                        </DetailsSection>
                    </OrderAside>
                </template>
            </LayoutWithAside>

        </Form>
    </div>
</template>

<script>
import LayoutWithAside from '@/components/LayoutWithAside';
import DetailsSection from '@/components/DetailsSection';
import TopSection from '@/components/checkout/TopSection';
import Teaser from '@/components/teasers/Teaser';
import DetailsTeaser from '@/components/teasers/DetailsTeaser';
import headMixin from '@/mixins/getHead';
import CancellationInitForm from '@/components/cancellation/CancellationInitForm';
import NonCancelableText from '@/components/cancellation/NonCancelableText';
import UnknownVoucherCode from '@/components/cancellation/UnknownVoucherCode';
import OrderAside from '@/components/checkout/OrderAside';
import Classification from '@/components/Classification';
import VariantData from '@/components/checkout/VariantData';
import HotelVoucherCancellation from '@/components/cancellation/HotelVoucherCancellation';
import ValueVoucherCancellation from '@/components/cancellation/ValueVoucherCancellation';
import SuccessText from '@/components/cancellation/SuccessText';
import Form from '@/components/Form';
import fetchResource from '@/utils/fetchResource';

export default {
    name: 'Cancellation',
    layout: ({ layout }) => layout,
    mixins: [headMixin],
    head() {
        return this.getHead({
            pageTitle: this.$t('Cancellation.title'),
            metaDescription: this.$t('Cancellation.metaDescription')
        });
    },
    data() {
        return {
            response: {},
            privilegedClient: false,
            loading: true,
            pending: false,
            success: false,
            errors: [],
            code: '',
            orderIdent: this.$route.query.orderIdent || null,
            cancellation: {
                refundMethod: 'Valuevoucher',
                cancelCompleteOrder: false,
                cancellationText: '',
                cancellationReason: '',
                firstname: '',
                lastname: '',
                email: '',
                iban: '',
                bic: ''
            }
        };
    },
    components: {
        UnknownVoucherCode,
        SuccessText,
        ValueVoucherCancellation,
        HotelVoucherCancellation,
        LayoutWithAside,
        DetailsSection,
        TopSection,
        Teaser,
        DetailsTeaser,
        CancellationInitForm,
        NonCancelableText,
        OrderAside,
        Classification,
        VariantData,
        Form
    },
    computed: {
        initialState() {
            return this.$isEmptyObject(this.response);
        },
        unknownVoucherCode() {
            return this.response && this.response.unknown;
        },
        nonCancelableState() {
            const states = ['cancelled', 'expired', 'consumed'];
            let result = states.find(state => !!this.response[state]);
            //console.table(this.response);
            if (result === undefined && !this.response.cancellationAvailable) {
                result = 'notCancellable';
            }
            if (this.response.productData && this.response.productData.arrivalDate && !this.response.cancellationAvailable) {
                result = 'notCancellableBooking';
            } else if (!this.response.cancellationAvailable && this.response.rejectedBid) {
                result = 'rejectedBid';
            }
            return result;
        },
        supportPhone() {
            return this.$store.state.globalMeta.supportPhoneNumber;
        },
        supportEmail() {
            return this.$store.state.globalMeta.supportEmailAddress;
        },
        variantData() {
            const { duration, persons, roomtype: roomType, catering } = this.response.productData;
            return {
                duration,
                persons,
                roomType,
                catering
            };
        },
        theme() {
            return this.$store.state.theme;
        },
        teaserImageUrl() {
            if (this.$store.state.webP) {
                return require('@/assets/webp/teasers/contact.webp');
            }
            return require('@/assets/teasers/contact.jpg');
        }
    },
    methods: {
        populateErrors(error) {
            if (error.response) {
                if (Array.isArray(error.response.data)) {
                    this.errors = error.response.data.map(e => e.text);
                } else {
                    this.errors = [error.message];
                }
            }
        },
        async performClientCheck() {
            const { data } = await this.$axios.get('/shop/client-check');
            this.privilegedClient = data.privilegedClient;
            if (this.orderIdent) {
                await this.getCancellationInfo();
            }
            this.loading = false;
        },
        async getCancellationInfo() {
            const { code, orderIdent } = this;
            const { data } = await this.$axios.post('/service/cancellation-info', {
                code: code,
                orderIdent: orderIdent
            });
            this.response = data;
            if (this.response.originalPaymentStatus === 'RefundableByValueVoucherOnly') {
                this.cancellation.refundMethod = 'Valuevoucher';
            } else if (this.response.valueVoucher) {
                this.cancellation.refundMethod = 'OriginalPaymentMethod';
            }
        },
        async submitCancellation() {
            const { cancellation } = this;
            if (cancellation.cancellationText && cancellation.cancellationText.length >= 5000) {
                this.errors = ['Stornierungsdetails - Eingabe ist zu lang'];
                return Promise.reject('');
            }
            const requestBody = {
                ...cancellation,
                code: this.response.code,
                orderIdent: this.orderIdent
            };
            await this.$axios.post('/service/cancellation', requestBody);
            this.success = true;
        },
        async conditionalSubmit(event) {
            try {
                //console.log(this.privilegedClient);
                this.pending = true;

                if (this.initialState) {
                    await this.getCancellationInfo();
                    this.$refs.form.removeValidation();
                } else if (event.target.elements['action'].value === 'cancel') {
                    await this.submitCancellation();
                }

                this.errors = [];
            } catch (e) {
                this.populateErrors(e);
            } finally {
                this.pending = false;
            }
        },
        sendGTMEvent() {
            this.$gtm && this.$gtm.pushPageView('cancellation');
        }
    },
    watch: {
        success(success) {
            if (success) {
                this.response.productData = null;
            }
        }
    },
    beforeMount() {
        this.sendGTMEvent();
    },
    mounted() {
        this.performClientCheck();
    }
};
</script>

<style scoped lang="scss">
#cancellation {
    .pending-block {
        background: color-mix(in srgb, var(--white), transparent 30%); //rgba($white, 0.7)
        position: absolute;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        top: 0;
        justify-content: center;
        z-index: 100;

        p {
            margin-bottom: 0;
            margin-left: 10px;
        }
    }
}
</style>

<style lang="scss">
@use '@/styles/variables';

#cancellation {
    .single-section {
        border: 1px solid var(--border-color);
        background-color: var(--stepper-step-bg);
        padding: variables.$grid-gutter-width variables.$grid-padding;
    }

    .section-header:not(:last-child) {
        margin-bottom: 1rem;
    }

    .section-header.title {
        font-size: 17px;
        font-weight: bold;
    }
}
</style>
